import { useMemo, useState } from 'react';
import { floorCalendar } from '../../data/floorCalendar';
import { floorDayFor, floorStates, sumStates } from '../../utils/floor';
import { resolveFloorRange, type FloorPreset } from '../../utils/floorRange';
import type { Agent, FloorStates } from '../../types/voip';
import type { CallsInsightHubState } from '../CallsInsightHub/useCallsInsightHub';

export type FloorSortKey = 'agent' | 'onCall' | 'waiting' | 'ready' | 'notReady' | 'loggedIn' | 'focus';
export type SortDir = 'asc' | 'desc';

export interface FloorAgentRow {
  agent: Agent;
  states: FloorStates;
}

export const FLOOR_PAGE_SIZE = 25;

export function useFloorTime(hub: CallsInsightHubState) {
  const [preset, setPresetState] = useState<FloorPreset>('yesterday');
  const [custom, setCustomState] = useState({ start: floorCalendar.max, end: floorCalendar.max });
  const [sort, setSort] = useState<{ key: FloorSortKey; dir: SortDir }>({ key: 'onCall', dir: 'desc' });
  const [query, setQueryState] = useState('');
  const [page, setPage] = useState(1);

  const range = useMemo(() => resolveFloorRange(preset, custom), [preset, custom]);

  const setPreset = (p: FloorPreset) => {
    setPresetState(p);
    setPage(1);
  };
  const setCustom = (next: { start: string; end: string }) => {
    setCustomState(next);
    setPage(1);
  };
  const setQuery = (q: string) => {
    setQueryState(q);
    setPage(1);
  };

  const toggleSort = (key: FloorSortKey) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'agent' ? 'asc' : 'desc' }));
    setPage(1);
  };

  // Every agent in the team filter, with floor time summed across the selected days
  const teamRows = useMemo<FloorAgentRow[]>(() => {
    const scoped = hub.team === 'All' ? hub.allRows : hub.allRows.filter((r) => r.agent.team === hub.team);
    return scoped.map(({ agent }) => ({
      agent,
      states: sumStates(range.dates.map((d) => floorStates(floorDayFor(agent, d)))),
    }));
  }, [hub.allRows, hub.team, range.dates]);

  const totals = useMemo(() => sumStates(teamRows.map((r) => r.states)), [teamRows]);
  const agentDays = teamRows.length * range.dates.length;

  const sortedRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? teamRows.filter((r) => r.agent.name.toLowerCase().includes(q) || r.agent.extension.includes(q))
      : teamRows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sort.key === 'agent') return a.agent.name.localeCompare(b.agent.name) * dir;
      return (a.states[sort.key] - b.states[sort.key]) * dir || a.agent.name.localeCompare(b.agent.name);
    });
  }, [teamRows, query, sort]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / FLOOR_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = sortedRows.slice((currentPage - 1) * FLOOR_PAGE_SIZE, currentPage * FLOOR_PAGE_SIZE);

  return {
    preset,
    setPreset,
    custom,
    setCustom,
    range,
    hasData: range.dates.length > 0,
    sort,
    toggleSort,
    query,
    setQuery,
    totals,
    agentCount: teamRows.length,
    agentDays,
    sortedRows,
    pageRows,
    page: currentPage,
    pageCount,
    setPage,
  };
}
